import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Info, Code, Heart, Github, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const AboutModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation('shared');
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100000] flex items-start justify-center pt-4 sm:pt-6 md:pt-8 pb-2 sm:pb-4 px-2 sm:px-4 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: -20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: -20 }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
            className="relative bg-[var(--color-bg-base)] p-4 sm:p-6 md:p-8 rounded-lg shadow-xl border border-white/10 max-w-2xl w-full max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={onClose}
              className="absolute top-2 right-2 sm:top-3 sm:right-3 p-1 rounded-full text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-secondary)] transition-colors"
              aria-label={t('aboutModal.close')}
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex flex-col items-center text-center mb-6">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-primary-600 to-primary-700 flex items-center justify-center mb-4 shadow-lg shadow-primary-600/30">
                <Info className="w-8 h-8 sm:w-10 sm:h-10 text-white" strokeWidth={2} />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[var(--color-text-primary)] mb-2">{t('aboutModal.appName')}</h2>
              <p className="text-sm sm:text-base text-[var(--color-text-secondary)] mb-1">{t('aboutModal.tagline')}</p>
              <p className="text-xs sm:text-sm text-[var(--color-text-muted)]">{t('aboutModal.version')}</p>
            </div>

            <div className="space-y-4 sm:space-y-5 text-sm sm:text-base text-[var(--color-text-secondary)]">
              <div>
                <h3 className="font-semibold text-[var(--color-text-primary)] mb-2 flex items-center gap-2">
                  <Code className="w-4 h-4" />
                  {t('aboutModal.aboutHeading')}
                </h3>
                <p className="text-[var(--color-text-secondary)] leading-relaxed">{t('aboutModal.aboutText')}</p>
              </div>

              <div>
                <h3 className="font-semibold text-[var(--color-text-primary)] mb-2 flex items-center gap-2">
                  <Heart className="w-4 h-4 text-red-500" />
                  {t('aboutModal.featuresHeading')}
                </h3>
                <ul className="list-disc list-inside space-y-1 text-[var(--color-text-secondary)] ml-2">
                  <li>{t('aboutModal.features.richEditor')}</li>
                  <li>{t('aboutModal.features.planning')}</li>
                  <li>{t('aboutModal.features.breaks')}</li>
                  <li>{t('aboutModal.features.chat')}</li>
                  <li>{t('aboutModal.features.themes')}</li>
                  <li>{t('aboutModal.features.sync')}</li>
                  <li>{t('aboutModal.features.backup')}</li>
                  <li>{t('aboutModal.features.importExport')}</li>
                </ul>
              </div>

              <div className="pt-4 border-t border-white/10">
                <p className="text-xs sm:text-sm text-[var(--color-text-muted)] text-center">
                  {t('aboutModal.copyright')}
                </p>

                <p className="text-xs sm:text-sm text-[var(--color-text-muted)] text-center mt-2">
                  {t('aboutModal.license')}
                </p>
                <p className="text-xs sm:text-sm text-center mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
                  <a
                    href="https://notesplus.bosph.net/privacy/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    {t('aboutModal.privacyPolicy')}
                  </a>
                  <a
                    href="https://notesplus.bosph.net/terms/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    {t('aboutModal.termsOfUse')}
                  </a>
                  <a
                    href="https://notesplus.bosph.net/community-guidelines/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    {t('aboutModal.communityGuidelines')}
                  </a>
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AboutModal;

